import { useState, useRef, Fragment, useEffect, useCallback } from "react";
import { TabItem, Tabs, Input, Dropdown, Button } from "components/atoms/index";
import { Icon } from "@/icons";
import { Dialog } from "@/components/organisms/Modal/Dialog";
import * as textarea from '@/components/atoms/Input/Input.css';
import { useLocation, useNavigate } from "react-router-dom";
import { getSlugPrefix } from '@/utils/slug';
import { academyInfoApi, academyTeacherApi, fileApi } from '@/services/api';
import { message } from "antd";
import { useSessionSlug } from '@/hooks/useSessionSlug';
import dayjs from 'dayjs';

const ACADEMY_SESSION_KEYS = [
    'academy_token',
    'academy_refresh_token',
    'academy_name',
    'academy_user_name',
    'academy_login_id',
    'academy_user_id',
    'academy_representative_email',
];

const clearAcademySession = () => {
    if (typeof window === 'undefined') return;
    ACADEMY_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

type PasswordFormState = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

type PasswordErrorState = PasswordFormState & {
    common: string;
};

const createEmptyPasswordForm = (): PasswordFormState => ({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
});

const createEmptyPasswordErrors = (): PasswordErrorState => ({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    common: '',
});

const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_PATTERN = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/;

const normalizeAcademyRole = (role?: string | null) => {
    // [QA Fix] 강사 마이페이지 역할 문자열을 통일해 팝업 노출 이슈 해결
    const normalized = role?.toString().trim().toLowerCase() || '';
    if (normalized.includes('teacher')) return 'teacher';
    if (normalized.includes('owner')) return 'owner';
    return normalized || 'owner';
};

const formatFullDateTime = (value?: string | null) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const showPasswordToast = (variant: 'success' | 'error', content: string) => {
    // [QA Fix] 비밀번호 유효성 안내를 한글 메시지와 충분한 노출 시간으로 통일
    message[variant]({ content, duration: 5 });
};

const isPasswordFormatValid = (value: string) => PASSWORD_PATTERN.test(value.trim());

// 탭 아이템 컴포넌트
export const AcademyMyPageTabItems = ({ activeTab, setActiveTab }: { 
    activeTab: 'info' | 'plan' | 'paper' | 'payment' | 'class'; 
    setActiveTab: (tab: 'info' | 'plan' | 'paper' | 'payment' | 'class') => void;     
}) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);
    const { role: sessionRole } = useSessionSlug();
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const effectiveRole = normalizeAcademyRole(sessionRole || storedRole || '');
    const isTeacher = effectiveRole === 'teacher';
    const tabItems: TabItem[] = [
        { id: 'info', label: '기본 정보', onClick: () => navigate(`${slugPrefix}/academy/mypage/info`) },
        { id: 'plan', label: '요금제 관리', onClick: () => navigate(`${slugPrefix}/academy/mypage/plan`) },
        { id: 'paper', label: '시험지 관리', onClick: () => navigate(`${slugPrefix}/academy/mypage/paper`) },
        { id: 'class', label: '클래스 관리', onClick: () => navigate(`${slugPrefix}/academy/mypage/class`) },
        { id: 'payment', label: '결제이력', onClick: () => navigate(`${slugPrefix}/academy/mypage/paymentHistory`) },
    ];
    const filteredTabs = isTeacher
        ? tabItems.filter((tab) => !['plan', 'class', 'payment', 'paper'].includes(tab.id))
        : tabItems;
    const effectiveActiveTab = filteredTabs.some((tab) => tab.id === activeTab)
        ? activeTab
        : (filteredTabs[0]?.id ?? activeTab);
    return <Tabs tabs={filteredTabs} activeId={effectiveActiveTab} />;
};

export const AcademyMyPageLogoutButton = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);

    const handleLogout = () => {
        clearAcademySession();
        navigate(`${slugPrefix}/academy/login`, { replace: true });
    };

    return (
        <Button size="small" intent="secondary" onClick={handleLogout}>
            로그아웃
        </Button>
    );
};

export default function AcademyMyPageInfo() {
    const [activeTab, setActiveTab] = useState<'info' | 'plan' | 'paper' | 'payment' | 'class'>('info');
    const [logoImage, setLogoImage] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null); // 업로드할 파일 저장
    const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [passwordForm, setPasswordForm] = useState<PasswordFormState>(() => createEmptyPasswordForm());
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrorState>(() => createEmptyPasswordErrors());
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const { role: sessionRole } = useSessionSlug();
    const storedRole = typeof window !== 'undefined'
        ? (localStorage.getItem('academy_role') || 'owner')
        : 'owner';
    const userType = normalizeAcademyRole(sessionRole || storedRole || 'owner');
    const [academy, setAcademy] = useState<any | null>(null);
    const getTeacherJoinDateLabel = () => formatFullDateTime(academy?.teacherCreatedAt || academy?.teacherProfile?.createdAt);
    const loginIdRef = (typeof window !== 'undefined')
        ? (localStorage.getItem('academy_login_id') || localStorage.getItem('academy_user_name') || '')
        : '';
    const getDisplayLoginId = () => {
        if (loginIdRef) return loginIdRef;
        if (academy?.loginId) return academy.loginId;
        if (academy?.representativeEmail) return academy.representativeEmail;
        return '';
    };
    
    // 입력 필드 상태 관리
    const [formData, setFormData] = useState({
        academyName: '',
        representativeName: '',
        representativePhone: '',
        representativeEmail: ''
    });

    const resetPasswordDialogState = () => {
        setPasswordForm(createEmptyPasswordForm());
        setPasswordErrors(createEmptyPasswordErrors());
        setIsPasswordChanging(false);
    };

    const handlePasswordFieldChange = (field: keyof PasswordFormState, value: string) => {
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        setPasswordErrors((prev) => ({ ...prev, [field]: '', common: '' }));
    };

    const validatePasswordForm = (form: PasswordFormState) => {
        const errors = createEmptyPasswordErrors();
        const currentPassword = form.currentPassword ?? '';
        const newPassword = form.newPassword ?? '';
        const confirmPassword = form.confirmPassword ?? '';

        if (!currentPassword.trim()) {
            errors.currentPassword = '현재 비밀번호를 입력해주세요.';
        }
        if (!newPassword.trim()) {
            errors.newPassword = '새 비밀번호를 입력해주세요.';
        } else if (newPassword.trim().length < MIN_PASSWORD_LENGTH) {
            errors.newPassword = `비밀번호는 최소 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.`;
        } else if (!isPasswordFormatValid(newPassword)) {
            errors.newPassword = '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.';
        }
        if (!confirmPassword.trim()) {
            errors.confirmPassword = '새 비밀번호를 다시 입력해주세요.';
        } else if (newPassword.trim() && newPassword.trim() !== confirmPassword.trim()) {
            errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
        }
        return errors;
    };

    const mapAcademyData = (raw: any) => {
        if (!raw) return raw;
        const teacherProfile = raw?.teacherProfile || {};
        return {
            ...raw,
            teacherProfile,
            teacherLoginId: teacherProfile.loginId || raw?.loginId || '',
            teacherName: teacherProfile.displayName || teacherProfile.name || raw?.representativeName || '',
            teacherBirthDate: teacherProfile.birthDate || '',
            teacherPhone: teacherProfile.phone || teacherProfile.contact || '',
        teacherCreatedAt: teacherProfile.createdAt || teacherProfile.hiredAt || '',
        };
    };

    // 이미지 업로드 핸들러 - 미리보기만 처리
    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // 파일 타입 검증
            if (!file.type.startsWith('image/')) {
                alert('이미지 파일만 업로드 가능합니다.');
                return;
            }
            
            // 파일 크기 검증 (5MB 제한)
            if (file.size > 5 * 1024 * 1024) {
                alert('파일 크기는 5MB 이하여야 합니다.');
                return;
            }

            // 파일 객체 저장 (실제 업로드는 저장 버튼 클릭 시)
            setLogoFile(file);
            
            // 미리보기 이미지 생성
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoImage(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // 이미지 추가 버튼 클릭 핸들러
    const handleAddImage = () => {
        fileInputRef.current?.click();
    };

    // 이미지 삭제 핸들러
    const handleDeleteImage = () => {
        setLogoImage(null);
        setLogoFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleWithdrawalConfirm = () => {
        // 탈퇴 처리 로직
        console.log('탈퇴 처리 완료');
        setShowWithdrawalDialog(false);
        // 여기에 실제 탈퇴 API 호출 로직 추가
    };

    const handleWithdrawalCancel = () => {
        setShowWithdrawalDialog(false);
    };

    // 비밀번호 변경 다이얼로그 핸들러들
    const handlePasswordChangeClick = () => {
        resetPasswordDialogState();
        setShowPasswordDialog(true);
    };

    const handlePasswordChangeCancel = () => {
        if (isPasswordChanging) return;
        resetPasswordDialogState();
        setShowPasswordDialog(false);
    };

    const handlePasswordChangeConfirm = async () => {
        if (isPasswordChanging) return;
        const validationErrors = validatePasswordForm(passwordForm);
        if (Object.values(validationErrors).some(Boolean)) {
            setPasswordErrors(validationErrors);
            const firstErrorMessage = validationErrors.currentPassword
                || validationErrors.newPassword
                || validationErrors.confirmPassword
                || validationErrors.common;
            if (firstErrorMessage) {
                showPasswordToast('error', firstErrorMessage);
            }
            return;
        }

        setPasswordErrors(createEmptyPasswordErrors());

        try {
            setIsPasswordChanging(true);
            const res: any = await academyInfoApi.changePassword(passwordForm);
            const success = res?.success ?? res?.data?.success;
            const responseMessage = res?.message || res?.data?.message;

            if (success) {
                showPasswordToast('success', responseMessage || '비밀번호가 변경되었습니다.');
                resetPasswordDialogState();
                setShowPasswordDialog(false);
            } else {
                const errorMessage = responseMessage || '비밀번호 변경에 실패했습니다.';
                const nextErrors = createEmptyPasswordErrors();
                nextErrors.common = errorMessage;
                setPasswordErrors(nextErrors);
                showPasswordToast('error', errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.';
            const nextErrors = createEmptyPasswordErrors();
            nextErrors.common = errorMessage;
            setPasswordErrors(nextErrors);
            showPasswordToast('error', errorMessage);
        } finally {
            setIsPasswordChanging(false);
        }
    };

    const loadAcademyInfo = useCallback(async () => {
            try {
                const res: any = await academyInfoApi.getMyInfo();
                const raw = res?.data || res;
                const mapped = mapAcademyData(raw);
                const teacherProfile = mapped?.teacherProfile || {};
                setAcademy(mapped);
                
            const defaultForm = {
                    academyName: mapped?.academyName || '',
                    representativeName: mapped?.representativeName || '',
                    representativePhone: mapped?.representativePhone || '',
                    representativeEmail: mapped?.representativeEmail || ''
            };

                if (userType === 'teacher') {
                    setFormData({
                        academyName: mapped?.academyName || '',
                        representativeName: teacherProfile.displayName || teacherProfile.name || '',
                        representativePhone: teacherProfile.phone || '',
                        representativeEmail: teacherProfile.email || ''
                    });
            } else {
                setFormData(defaultForm);
                }
                
                if (mapped?.logoUrl) {
                    setLogoImage(mapped.logoUrl);
                } else {
                    setLogoImage(null);
                }
            } catch (e) {
                console.error('학원 정보 조회 실패:', e);
                alert('학원 정보를 불러오는데 실패했습니다.');
            }
    }, [userType]);

    // 학원 기본 정보 로드
    useEffect(() => {
        loadAcademyInfo();
    }, [loadAcademyInfo]);
    
    const handleTeacherSave = async () => {
        const teacherId = academy?.teacherProfile?.teacherId;
        if (!teacherId) {
            message.error('강사 정보를 불러오지 못했습니다.');
            return;
        }
        if (!formData.representativeName.trim()) {
            message.error('이름을 입력해주세요.');
            return;
        }
        try {
            setIsSaving(true);
            await academyTeacherApi.update(teacherId, {
                name: formData.representativeName.trim(),
                phone: formData.representativePhone.trim()
            });
            message.success('정보가 변경되었습니다.');
            await loadAcademyInfo();
        } catch (error: any) {
            const msg = error?.response?.data?.message || '정보 수정에 실패했습니다.';
            message.error(msg);
        } finally {
            setIsSaving(false);
        }
    };
    
    // 저장 버튼 핸들러
    const handleSave = async () => {
        if (isSaving) return;
        if (userType === 'teacher') {
            await handleTeacherSave();
            return;
        }
        
        try {
            setIsSaving(true);
            
            let logoUrl = academy?.logoUrl || null;
            
            // 새로운 로고 파일이 있으면 S3에 업로드
            if (logoFile) {
                try {
                    const uploadRes = await fileApi.upload(logoFile, 'academy-logos');
                    logoUrl = uploadRes?.data?.url || uploadRes?.url;
                } catch (uploadError) {
                    console.error('로고 업로드 실패:', uploadError);
                    alert('로고 이미지 업로드에 실패했습니다.');
                    setIsSaving(false);
                    return;
                }
            }
            
            // 로고가 삭제된 경우
            if (!logoImage && !logoFile) {
                logoUrl = null;
            }
            
            // 학원 정보 업데이트
            await academyInfoApi.updateMyInfo({
                academyName: formData.academyName,
                representativeName: formData.representativeName,
                representativePhone: formData.representativePhone,
                representativeEmail: formData.representativeEmail,
                logoUrl: logoUrl
            });
            
            message.success('정보가 변경되었습니다.');
            await loadAcademyInfo();
            setLogoFile(null);
            
        } catch (error) {
            console.error('저장 실패:', error);
            alert('저장에 실패했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const mapRegistrationStatus = (s?: string) => {
        if (!s) return '-';
        const m: Record<string, string> = { PENDING: '대기', APPROVED: '승인', WITHDRAWN: '탈퇴', REJECTED: '거절' };
        return m[s] || s;
    };
    const contractStatusLabels: Record<string, string> = {
        free_usage: '무료사용',
        payment_waiting: '결제대기',
        payment_active: '결제중',
        payment_failed: '결제실패',
        cancelled: '해지',
        withdrawal_pending: '탈퇴신청',
    };
    const normalizeStatusKey = (value?: string | null) => (value ? value.toString().trim().toLowerCase() : '');
    const getContractStatusLabel = () => {
        if (!academy) return '-';

        const customLabel = academy.contractStatusLabel?.toString().trim();
        if (customLabel && Object.values(contractStatusLabels).includes(customLabel)) {
            return customLabel;
        }

        const keyFromApi = normalizeStatusKey(academy.contractStatusKey);
        if (keyFromApi && contractStatusLabels[keyFromApi]) {
            return contractStatusLabels[keyFromApi];
        }

        const fallbackKey = normalizeStatusKey(academy.contractStatus);
        if (fallbackKey && contractStatusLabels[fallbackKey]) {
            return contractStatusLabels[fallbackKey];
        }

        return '-';
    };
    const fmt = (v?: any) => (v == null || v === '' ? '-' : String(v));

    return (
        <Fragment>
            <main className='flex flex-col flex-1'>
                <div className='flex flex-row justify-between items-center pt-8'>
                    <h1 className='heading1'>마이페이지</h1>
                    <AcademyMyPageLogoutButton />
                </div>
                <div className="mt-5">
                    <AcademyMyPageTabItems activeTab={activeTab} setActiveTab={setActiveTab} />
                    <div className="py-5">
                        <div className="w-full">
                            <ul>
                                {userType === 'teacher' ? (
                                    // 강사 필드들
                                    <>
                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>아이디</p>
                                            <Input disabled value={academy?.teacherLoginId || loginIdRef || ''} className='max-w-[500px] w-full'/>
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>이름</p>
                                            <Input 
                                                placeholder='이름을 입력해주세요' 
                                                className='max-w-[500px] w-full' 
                                                value={formData.representativeName} 
                                                onChange={(e) => setFormData({...formData, representativeName: e.target.value})}
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>생년월일</p>
                                            <Input placeholder='생년월일을 입력해주세요 (YYYY-MM-DD)' className='max-w-[500px] w-full' value={academy?.teacherBirthDate || ''} readOnly />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>연락처</p>
                                            <Input
                                                placeholder='연락처를 입력해주세요'
                                                className='max-w-[500px] w-full'
                                                maxLength={30}
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                value={formData.representativePhone}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\\D/g, '');
                                                    setFormData({ ...formData, representativePhone: digits });
                                                }}
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>비밀번호 관리</p>
                                            <Button onClick={handlePasswordChangeClick}>비밀번호 변경</Button>
                                        </li>
                                    </>
                                ) : (
                                    // 원장 필드들
                                    <>
                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>아이디</p>
                                            <Input disabled value={getDisplayLoginId()} className='max-w-[500px] w-full'/>
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>학원명</p>
                                            <Input 
                                                placeholder='학원명을 입력해주세요' 
                                                maxLength={100}
                                                value={formData.academyName} 
                                                onChange={(e) => setFormData({...formData, academyName: e.target.value})}
                                                className='max-w-[500px] w-full'
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>대표자명</p>
                                            <Input 
                                                placeholder='대표자명을 입력해주세요' 
                                                maxLength={100}
                                                value={formData.representativeName} 
                                                onChange={(e) => setFormData({...formData, representativeName: e.target.value})}
                                                className='max-w-[500px] w-full'
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>대표자 연락처</p>
                                            <Input 
                                                placeholder='대표자 연락처를 입력해주세요' 
                                                maxLength={30}
                                                inputMode='numeric'
                                                pattern='[0-9]*'
                                                value={formData.representativePhone} 
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\\D/g, '');
                                                    setFormData({...formData, representativePhone: digits});
                                                }}
                                                className='max-w-[500px] w-full'
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>대표자 이메일</p>
                                            <Input 
                                                placeholder='대표자 이메일을 입력해주세요' 
                                                maxLength={120}
                                                value={formData.representativeEmail} 
                                                onChange={(e) => setFormData({...formData, representativeEmail: e.target.value})}
                                                className='max-w-[500px] w-full'
                                            />
                                        </li>

                                        <li className='flex py-5 border-b border-gray-100'>
                                            <p className='flex-none max-w-[150px] w-full text-gray-500'>사업자 등록번호</p>
                                            <Input disabled value={academy?.businessNumber || ''} className='max-w-[500px] w-full'/>
                                        </li>
                                    </>
                                )}
                            </ul>

                            {/* 학원 정보 */}
                            <section className="py-5">
                                <p className="mb-3 font-bold text-gray-900">학원 정보</p>
                                <ul>
                                    <li className="flex py-5 border-b border-gray-100">
                                        <p className='flex-none max-w-[150px] w-full text-gray-500'>학원코드</p>
                                        <p>{fmt(academy?.academyCode)}</p>
                                    </li>
                                    {userType === 'teacher' ? (
                                        // 강사용 학원 정보
                                        <>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>가입 상태</p>
                                                <p>승인</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>강사 상태</p>
                                                <p>정상</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>가입 신청일</p>
                                                <p>{getTeacherJoinDateLabel()}</p>
                                            </li>
                                        </>
                                    ) : (
                                        // 원장용 학원 정보
                                        <>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>사용 인원</p>
                                                <p>{fmt(academy?.totalUsers)}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>실 사용인원</p>
                                                <p>{fmt(academy?.activeUsers)}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>학원 URL</p>
                                                <p>{fmt(academy?.academyUrl)}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>로고 이미지</p>
                                                <div className="max-w-[500px] w-full">
                                                    <div className="flex flex-col gap-2 justify-center items-center rounded-md border border-gray-200 aspect-[4/1] relative">
                                                        {logoImage ? (
                                                            <img src={logoImage} alt="로고" className="object-contain p-2 w-full h-full rounded-md" />
                                                        ) : (
                                                            <div className="flex flex-col gap-2 justify-center items-center py-5">
                                                                <Icon name="image" size={24} className="text-gray-400" />
                                                                <p className="text-sm text-gray-500">로고 이미지가 없습니다</p>
                                                                <Button size="small" className="w-auto" onClick={handleAddImage}>이미지 추가</Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col gap-2 items-start mt-2">
                                                        <p className="text-sm text-gray-500">· 권장 규격: 정사각형 PNG/JPG, 최소 400px 이상 (배경 투명 권장)</p>
                                                        <p className="text-sm text-gray-500">· 파일 크기는 2MB 이하로 업로드해주세요. 교체 시 기존 이미지는 즉시 대체됩니다.</p>
                                                        <div className="flex gap-2">
                                                            {logoImage && (
                                                                <>
                                                                    <Button size="small" outline onClick={handleAddImage}>이미지 변경</Button>
                                                                    <Button size="small" intent="danger" outline onClick={handleDeleteImage}>사진 삭제</Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        className="hidden"
                                                    />
                                                </div>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>가입상태</p>
                                                <p>{mapRegistrationStatus(academy?.registrationStatus)}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>계약상태</p>
                                                <p>{getContractStatusLabel()}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>가입 신청일</p>
                                                <p>{fmt(academy?.applicationDate)}</p>
                                            </li>
                                            <li className="flex py-5 border-b border-gray-100">
                                                <p className='flex-none max-w-[150px] w-full text-gray-500'>가입 승인일</p>
                                                <p>{fmt(academy?.registrationDate)}</p>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </section>
                            
                            {/* 버튼 */}
                            <div className='flex justify-end *:w-[120px] gap-2 mt-4'>
                                <Button intent='secondary' onClick={() => window.location.reload()}>취소</Button>
                                <Button onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? '저장 중...' : '저장'}
                                </Button>
                            </div>
                            {/* <Button text intent="secondary" className="mx-auto my-5 underline" onClick={handleWithdrawalClick}>알파영어 탈퇴신청</Button> */}
                        </div>
                    </div>
                </div>
            </main>

            {/* 비밀번호 변경 다이얼로그 */}
            {showPasswordDialog && (
                <Dialog
                    hideFooter
                    onCancel={handlePasswordChangeCancel}
                >
                    <div className='p-5 pb-0 w-[min(90vw,420px)]'>
                        <p className='mb-4 text-lg font-bold text-gray-900'>비밀번호 변경</p>
                        <div className='space-y-4'>
                            <div>
                                <Input
                                    label="현재 비밀번호"
                                    type="password"
                                    placeholder='현재 비밀번호를 입력해주세요'
                                    value={passwordForm.currentPassword}
                                    autoComplete='current-password'
                                    onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
                                />
                                {passwordErrors.currentPassword && (
                                    <p className='mt-1 text-xs text-red-500'>{passwordErrors.currentPassword}</p>
                                )}
                            </div>
                            <div>
                                <Input
                                    label="새 비밀번호"
                                    type="password"
                                    placeholder='새 비밀번호를 입력해주세요'
                                    value={passwordForm.newPassword}
                                    autoComplete='new-password'
                                    onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                                />
                                <p className='mt-1 text-xs text-gray-500'>비밀번호는 최소 {MIN_PASSWORD_LENGTH}자 이상이어야 합니다.</p>
                                {passwordErrors.newPassword && (
                                    <p className='mt-1 text-xs text-red-500'>{passwordErrors.newPassword}</p>
                                )}
                            </div>
                            <div>
                                <Input
                                    label="새 비밀번호 확인"
                                    type="password"
                                    placeholder='새 비밀번호를 다시 입력해주세요'
                                    value={passwordForm.confirmPassword}
                                    autoComplete='new-password'
                                    onChange={(e) => handlePasswordFieldChange('confirmPassword', e.target.value)}
                                />
                                {passwordErrors.confirmPassword && (
                                    <p className='mt-1 text-xs text-red-500'>{passwordErrors.confirmPassword}</p>
                                )}
                            </div>
                            {passwordErrors.common && (
                                <p className='text-xs text-red-500'>{passwordErrors.common}</p>
                            )}
                        </div>
                        <div className='flex gap-2 justify-end pt-6 pb-6'>
                            <Button intent='secondary' onClick={handlePasswordChangeCancel} disabled={isPasswordChanging}>
                                취소
                            </Button>
                            <Button onClick={handlePasswordChangeConfirm} disabled={isPasswordChanging}>
                                {isPasswordChanging ? '변경 중...' : '변경'}
                            </Button>
                        </div>
                    </div>
            </Dialog>
            )}
        </Fragment>
    )
}